import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["resultMLOptionId"];
import { useMemo, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Section } from 'foundations-components/transitional/components/Section';
import ApiError from '../../../components/Errors/ApiError';
import NoResultsError from '../../../components/Errors/NoResultsError';
import ResultsStatusBar from '../../../components/ResultsStatusBar';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useDisplayedData } from '../../table';
import { PAGE_SIZE } from '../../pagination';
import { LearningContentCard } from '../../cards';
import { useCatalogSearchResults } from '../searchHooks';
import { SEARCH_FILTERS } from '../searchConsts';
import { useSearchFeedback } from '../searchQueries';
import Raven from 'raven-js';
import { useSearchState, useAiSearchSessionState } from '../searchStore';
import { scrollPageToTop } from 'academy-common-lib/utils/scrolling';
import { FiltersCard } from '../../filters/components/FiltersCard';
import { CertificationsFilter } from '../../filters/components/CertificationsFilter';
import { DurationFilter } from '../../filters/components/DurationFilter';
import { AllContentTypesFilter } from '../../filters/components/AllContentTypesFilter';
import { LevelFilter } from '../../filters/components/LevelFilter';
import { LanguageFilter } from '../../filters/components/LanguageFilter';
import { PageResultsCount } from '../../header/components/PageResultsCount';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AISearchQueryAnswer from '../../academyAi/components/AISearchQueryAnswer';
import { useIsVariation } from '../../experiments/experimentsQueries';
import { ContentSourceFilter } from '../../filters/components/ContentSourceFilter';
import { HasGate } from '../../accounts/components/HasGate';
import { usePageTitle } from '../../../hooks/usePageTitle';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SEARCH_RESULTS_SIZE = PAGE_SIZE;
const getCatalogTitleI18n = (search, category, tools) => {
  if (search) {
    return {
      key: 'learningCenter.dynamicPageTitles.catalogSearch',
      options: {
        searchTerm: search
      }
    };
  }
  if (category) {
    const categoryKey = `learningCenter.topFilters.cat.${category}`;
    const categoryName = I18n.text(categoryKey);
    return {
      key: 'learningCenter.dynamicPageTitles.catalogCategory',
      options: {
        categoryName: categoryName && categoryName !== categoryKey ? categoryName : category
      }
    };
  }
  if (tools) {
    const toolKey = `learningCenter.topFilters.tools.${tools}`;
    const toolName = I18n.text(toolKey);
    return {
      key: 'learningCenter.dynamicPageTitles.catalogTool',
      options: {
        toolName: toolName && toolName !== toolKey ? toolName : tools
      }
    };
  }
  return {};
};
const useIsAiSearchEnabled = () => {
  const isExperimentEnabled = useIsVariation('acad-0037');
  const {
    overrideAiSearchEnabled,
    setOverrideAiSearchEnabled
  } = useAiSearchSessionState();
  const [[overrideAiSearchEnabledParam]] = useSearchParam('aiSearchResults');
  useEffect(() => {
    if (overrideAiSearchEnabledParam === 'true') {
      setOverrideAiSearchEnabled(true);
    }
  }, [overrideAiSearchEnabledParam, setOverrideAiSearchEnabled]);
  return isExperimentEnabled || overrideAiSearchEnabled;
};
export const CatalogSearchResults = ({
  banners,
  isPartnerPage,
  pageUrlFragment
}) => {
  const isAiSearchEnabled = useIsAiSearchEnabled();
  const {
    results,
    loading,
    error,
    totalCount,
    page,
    setPage,
    searchCriteria,
    correlationId
  } = useCatalogSearchResults({
    partnersOnly: isPartnerPage,
    pageSize: SEARCH_RESULTS_SIZE
  });
  const [sendSearchFeedback] = useSearchFeedback();
  const {
    setLastClickedSearchResultItem
  } = useSearchState();
  const [[category]] = useSearchParam(FILTER_TYPES.CATEGORY);
  const [[tools]] = useSearchParam(FILTER_TYPES.TOOLS);
  const {
    key: catalogTitleKey,
    options: catalogTitleOptions
  } = useMemo(() => getCatalogTitleI18n(searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.search, category, tools), [searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.search, category, tools]);
  usePageTitle({
    i18nKey: catalogTitleKey,
    options: catalogTitleOptions,
    skip: !catalogTitleKey
  });
  const {
    results: displayedSearchResults,
    totalCount: displayedTotalCount = 0,
    searchCriteria: displayedSearchCriteria,
    category: displayedCategory,
    tools: displayedTools
  } = useDisplayedData({
    results,
    totalCount,
    searchCriteria,
    category,
    tools
  }, {
    loading,
    parameters: {
      page,
      searchCriteria
    }
  }) || {};
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const handlePageChange = event => {
    setPage(event.target.value);
    scrollPageToTop();
  };
  const handleOnItemClick = (_ref, position) => {
    let {
        resultMLOptionId
      } = _ref,
      item = _objectWithoutPropertiesLoose(_ref, _excluded);
    setLastClickedSearchResultItem({
      item: {
        id: item.id,
        type: item.type,
        language: item.language
      },
      position
    });
    if (!correlationId) return;
    sendSearchFeedback({
      variables: {
        correlationId,
        contentContainerType: item.type,
        resultMLOptionId
      }
    }).catch(Raven.captureException.bind(Raven));
  };
  const resultsContent = /*#__PURE__*/_jsx(_Fragment, {
    children: !displayedSearchResults && loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 250,
      size: "medium"
    }) : error ? /*#__PURE__*/_jsx(ApiError, {
      error: error,
      marker404: "CATALOG_COURSES_API_404",
      markerDefault: "CATALOG_COURSES_API_ERROR",
      resetLink: pageUrlFragment
    }) : !(displayedSearchResults !== null && displayedSearchResults !== void 0 && displayedSearchResults.length) && !loading ? /*#__PURE__*/_jsx(NavMarker, {
      name: "CATALOG_COURSES_LOADED",
      children: /*#__PURE__*/_jsx(NoResultsError, {
        resetLink: pageUrlFragment
      })
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "CATALOG_COURSES_LOADED",
      children: /*#__PURE__*/_jsxs(View, {
        "data-test-id": "main-catalog-cards-container",
        marginBlockStart: isMobile ? '0' : '400',
        children: [/*#__PURE__*/_jsx(Section, {
          children: /*#__PURE__*/_jsx(CardGrid, {
            gap: isMobile ? '200' : '600',
            gridTemplateColumns: isTablet ? '1fr' : 'repeat(auto-fill, minmax(296px, 1fr))',
            children: displayedSearchResults === null || displayedSearchResults === void 0 ? void 0 : displayedSearchResults.map((item, index) => /*#__PURE__*/_jsx(LearningContentCard, {
              extraTrackingInfo: {
                source: 'searchV2'
              },
              item: item,
              onItemClick: () => handleOnItemClick(item, index + 1),
              overviewType: "filterable-results",
              position: index + 1,
              size: isTablet ? 'small' : 'large'
            }, `item_${item.id}`))
          })
        }), /*#__PURE__*/_jsx(Section, {
          children: /*#__PURE__*/_jsx(Paginator, {
            onPageChange: handlePageChange,
            page: page,
            pageCount: Math.ceil(totalCount / SEARCH_RESULTS_SIZE),
            showFirstLastButtons: true
          })
        })]
      })
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [banners, isAiSearchEnabled && /*#__PURE__*/_jsx(AISearchQueryAnswer, {
      query: searchCriteria.search
    }), !isMobile && /*#__PURE__*/_jsxs(_Fragment, {
      children: [displayedSearchResults && /*#__PURE__*/_jsx(PageResultsCount, {
        page: page - 1,
        pageSize: SEARCH_RESULTS_SIZE,
        searchCriteria: {
          search: displayedSearchCriteria === null || displayedSearchCriteria === void 0 ? void 0 : displayedSearchCriteria.search,
          category: displayedCategory,
          hubspotSoftware: displayedTools
        },
        total: displayedTotalCount
      }), /*#__PURE__*/_jsx(HasGate, {
        gate: "Academy:TrainerContent",
        children: /*#__PURE__*/_jsx(ContentSourceFilter, {
          appearsIn: "results"
        })
      }), /*#__PURE__*/_jsxs(FiltersCard, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.myLearning.filterBy"
        }), /*#__PURE__*/_jsx(CertificationsFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(LevelFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(DurationFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(AllContentTypesFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(LanguageFilter, {
          appearsIn: "results"
        })]
      })]
    }), isMobile && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        borderBlockEndColor: "border-core-subtle",
        borderBlockEndStyle: "solid",
        borderBlockEndWidth: "100",
        marginBlockEnd: "200",
        marginBlockStart: "-400",
        marginInline: "-400",
        children: /*#__PURE__*/_jsx(ResultsStatusBar, {
          cardsTotal: displayedTotalCount,
          endCard: Math.min(page * PAGE_SIZE, displayedTotalCount),
          loading: !displayedSearchResults && loading,
          mobileFilterTypes: SEARCH_FILTERS,
          startCard: (page - 1) * PAGE_SIZE + 1,
          sticky: true
        })
      }), /*#__PURE__*/_jsx(HasGate, {
        gate: "Academy:TrainerContent",
        children: /*#__PURE__*/_jsx(ContentSourceFilter, {
          appearsIn: "results"
        })
      })]
    }), resultsContent]
  });
};